import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Network, Cpu, X } from "lucide-react";

// Video link
const cloudVideo = "https://res.cloudinary.com/dpn3bkjba/video/upload/v1758981863/ved1_g4j60q.mp4";

const sections = [
  {
    title: "360° Consulting",
    // Changed icon color to be brighter on the new dark card
    icon: <ShieldCheck className="text-green-400 w-10 h-10" />,
    description: `What we do here is to comprehensively study the enterprise network and data with steps such as data discovery and data classification, current security infrastructure the security threat posed by if any through penetration testing, and understand the need of the hour and provide a comprehensive report by top data analyst mind and architects and provide a detailed report.`,
    bulletPoints: [
      "Based on this report we would tailor a comprehensive solution to securely meet your requirements and to stay a step ahead.",
    ],
    video: cloudVideo,
  },
  {
    title: "A Unified Security Fabric, Not a Patchwork",
    icon: <Network className="text-green-400 w-10 h-10" />,
    description: ` We believe the industry's focus on single-solution leadership is flawed. True resilience comes from a unified, intelligent fabric. Our platform is a converged ecosystem of SASE, Cloud Security Posture Management, Advanced Threat Intelligence, and Identity and Browser Security, ensuring seamless, end-to-end protection across your network, cloud, and digital identity.`,
  },
  {
    title: "Built for the AI Era",
    icon: <Cpu className="text-green-400 w-10 h-10" />,
    description: `While other platforms adapt to AI, ours was born from it. Our AI-first architecture provides autonomous, predictive defense from the ground up, not as a feature. This allows our platform to continuously learn and defend against threats that others are still cataloging.`,
  },
];

const featureCards = sections;

function WhySynthera() {
  const [showVideo, setShowVideo] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } },
  };

  return (
    <div className="relative bg-white text-gray-800 py-28 px-6 lg:px-20 overflow-hidden">
      {/* SECTION 1 */}
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center mb-32">
        <motion.div
          className="relative group aspect-video bg-gray-100 border border-green-300 rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          whileHover={{ scale: 1.05 }}
        >
          <video
            src={cloudVideo}
            controls
            loop
            className="w-full h-full object-cover rounded-3xl"
          />
          <div className="absolute top-4 right-4 bg-white/80 text-gray-800 px-3 py-1 rounded-full text-sm font-semibold border border-gray-200">
            ▶ Video
          </div>
        </motion.div>

        <motion.div
          className="text-center lg:text-left"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-black">
            Real Stories, <span className="text-green-600">Real Success</span>
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto lg:mx-0">
            You have a choice: a collection of point solutions, or a future-proof
            security partner. Synthera provides a more complete, forward-looking
            answer to the complexities of modern security.
          </p>
        </motion.div>
      </div>

      {/* SECTION 2 */}
      <div className="max-w-7xl mx-auto">
        <motion.h2
          className="text-center text-4xl lg:text-5xl font-bold mb-16 text-black"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
        >
          Why Today's Leading Enterprises{" "}
          <span className="text-green-600">Choose Synthera</span>
        </motion.h2>

        <motion.div
          className="relative flex flex-col gap-20 max-w-5xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="absolute left-1/2 top-0 w-1 h-full bg-gradient-to-b from-green-400/50 to-transparent transform -translate-x-1/2" />

          {featureCards.map((card, i) => (
            <motion.div
              key={i}
              className={`relative bg-gradient-to-br from-green-900 to-teal-900 
                               border border-green-700 p-10 rounded-3xl shadow-xl
                               hover:shadow-[0_0_35px_rgba(16,185,129,0.5)] transition-shadow duration-300
                               md:w-1/2 ${i % 2 === 0 ? "md:ml-0 md:mr-auto" : "md:ml-auto md:mr-0"}`}
              whileHover={{ rotateY: 3, scale: 1.03 }}
              variants={itemVariants}
            >
              <span
                className={`absolute top-1/2 w-5 h-5 rounded-full bg-green-500 shadow-[0_0_20px_rgba(74,222,128,0.8)]
                                 ${i % 2 === 0 ? "md:-right-10" : "md:-left-10"} transform -translate-y-1/2 hidden md:block`}
              />
              <div className="flex items-center gap-4 mb-6">
                {card.icon}
                <h3 className="text-2xl font-semibold text-green-400 tracking-wide">
                  {card.title}
                </h3>
              </div>
              <p className="leading-relaxed text-gray-300 text-lg mb-4">{card.description}</p>
              {card.bulletPoints && (
                <ul className="list-disc list-inside text-gray-300 text-lg mb-6 pl-2 space-y-2">
                  {card.bulletPoints.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              )}
              {card.video && (
                <button
                  onClick={() => setShowVideo(i)}
                  className="px-6 py-2 bg-green-500 text-black font-bold rounded-full shadow-md hover:bg-green-400 transition-all transform hover:scale-105"
                >
                  ▶ Watch Now
                </button>
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {showVideo !== null && (
          <motion.div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowVideo(null)}
          >
            <motion.div
              className="relative w-full max-w-4xl bg-black rounded-2xl shadow-2xl border border-green-500/50 overflow-hidden"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowVideo(null)}
                className="absolute top-3 right-3 text-white bg-black/50 rounded-full p-2 z-10 hover:bg-green-500 transition-colors"
                aria-label="Close video player"
              >
                <X size={20} />
              </button>
              <video
                src={featureCards[showVideo].video}
                controls
                autoPlay
                loop
                className="w-full aspect-video"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default WhySynthera;